import {
  getItemOrDefaultToValue,
  getPersonName,
  transformArrayDisplay,
  createDisplay,
  createURL,
  nestedDisplay,
} from '../../details';
import { displayTime, formatTimeDifference } from '../../time';
import {
  BASE_EVENT_STATUS,
  BASE_EVENT_DISTRIBUTION,
  BASE_INCIDENT_STATUS,
} from 'utils/constants/eventStatus';
import {
  getLocationValue,
  editableLocation,
  assignUniqueId,
} from './eventTransformHelper';
import { audienceList } from 'mocks/utils';
import {
  converSeverityRatingToText,
  severityRatingEnums,
} from 'containers/Pages/Events/Layouts';
import isEmpty from 'lodash/isEmpty';
import { formatRoleNames } from 'utils/constants/roles';
import { DEFAULT_EMPTY_VALUE } from 'utils/constants';
import { t } from 'i18next';
import {
  AuditTableColumns,
  FollowupReprtTableColumns,
} from 'utils/columns/incidentForm';
import {
  formatTaskCategoryName,
  FollowupTaskTableHeaders,
} from 'utils/constants/task';
import { typeNameConstructor } from 'utils/DataTransform/Asset/AssetList';

// This is required because for recommendations, backend needs audience list ids as INTERNAL_AUDIENCE, CLIENT_AUDIENCE and PUBLIC_AUDIENCE
// and we need to display it as Internal Audience, Client Audience and Public Audience
export const transformRecommendations = vals => {
  return vals.map(recommendation => {
    return {
      ...recommendation,
      audience: {
        name: audienceList.filter(f => f.id === recommendation.audience)[0]
          .name,
        id: audienceList.filter(f => f.id === recommendation.audience)[0].id,
      },
    };
  });
};
export const transformRecommendationsEnum = vals => {
  return vals.map(recommendation => {
    return {
      ...recommendation,
      audience: audienceList.filter(f => f.id === recommendation.audience)[0]
        .name,
    };
  });
};

export const transform = (data, timeZone) => {
  const followupReports = data.followupEvents
    ? data.followupEvents.map(item => ({
        id: item.general.id, // add id key to detail view object if you want it to be clickable from detail panel
        eventId: item.general.eventId,
        title: item.general.title,
        eventCategory: t(item.general.category),
        eventType: t(item.general.type),
        eventStartTime: item.general.eventStartTime,
        eventFinishTime: item.general.eventFinishTime,
      }))
    : [];
  return {
    generalInfo: {
      followupEvents: {
        value: followupReports,
      },
      eventId: {
        label: 'Report ID',
        type: 'text',
        value: getItemOrDefaultToValue(data.general, 'eventId', '-'),
      },
      eventCategory: {
        type: 'text',
        value: getItemOrDefaultToValue(data.general, 'category', '-'),
      },
      title: {
        type: 'text',
        value: getItemOrDefaultToValue(data.general, 'title', '-'),
      },
      eventType: {
        type: 'text',
        value: {
          id: getItemOrDefaultToValue(data.general, 'type', '-'),
          typeName: getItemOrDefaultToValue(data.general, 'type', '-'),
        },
      },
      securityCompanyName: {
        type: 'text',
        value: getItemOrDefaultToValue(
          data.general,
          'securityCompanyName',
          '-',
        ),
      },
      lastUpdatedAt: {
        type: 'text',
        value: displayTime({
          timestamp: getItemOrDefaultToValue(data.publishing, 'lastUpdatedAt'),
          timeZone,
        }),
      },
      createdBy: {
        type: 'text',
        value:
          data.general.creator != null
            ? getPersonName(
                getItemOrDefaultToValue(data.general.creator, 'general', '-'),
              )
            : '-',
      },
      elapsedTime: {
        type: 'text',
        value: formatTimeDifference(
          getItemOrDefaultToValue(data.general, 'eventStartTime', '-'),
          getItemOrDefaultToValue(data.general, 'eventFinishTime'),
        ),
      },
      incidentStatus: {
        type: 'text',
        value: data.general.incidentStatus
          ? BASE_INCIDENT_STATUS[data.general.incidentStatus].value
          : '',
      },
      credibility: {
        type: 'rating',
        value: getItemOrDefaultToValue(data.general, 'credibility', 0),
      },
      distribution: {
        type: 'text',
        value: data.publishing.distribution
          ? BASE_EVENT_DISTRIBUTION[data.publishing.distribution].value
          : '',
      },
      publishStatus: {
        type: 'text',
        value: data.publishing.currentStatus
          ? BASE_EVENT_STATUS[data.publishing.currentStatus].value
          : '',
      },
      severity: {
        type: 'text',
        label: 'Severity Description',
        value:
          data.general.severity !== null && data.general.severity !== ''
            ? getItemOrDefaultToValue(data.general, 'severity', '')
            : '',
      },
      severityRating: {
        type: 'text',
        value:
          data.general.severityRating !== null &&
          data.general.severityRating !== ''
            ? getItemOrDefaultToValue(
                data.general,
                'severityRating',
                severityRatingEnums.notRated,
              )
            : severityRatingEnums.notRated,
      },
      archived: {
        type: 'text',
        value: getItemOrDefaultToValue(data.general, 'archived'),
      },
    },
    detailedInfo: {
      reporters: {
        type: 'array',
        value: createDisplay(
          getItemOrDefaultToValue(data.details.sources, 'reporters', []),
          ['jobRole', 'firstName', 'lastName'],
        ),
      },
      websites: {
        type: 'array',
        value: createURL(
          getItemOrDefaultToValue(data.details.sources, 'websites', []),
          ['url'],
        ),
      },
      involvedPeople: {
        type: 'array',
        value: createDisplay(
          getItemOrDefaultToValue(data.details, 'involvedPeople', []),
          ['involvement', 'firstName', 'lastName'],
        ),
      },
      involvedOrganisation: {
        type: 'array',
        value: nestedDisplay(
          getItemOrDefaultToValue(data.details, 'involvedCompanies', []),
        ),
      },
      involvedAssets: {
        type: 'array',
        value: nestedDisplay(
          getItemOrDefaultToValue(data.details, 'involvedAssets', []),
        ),
      },
      publicDescription: {
        type: 'text',
        value: getItemOrDefaultToValue(
          data.general.descriptions,
          'publicDescription',
          '-',
        ),
      },
      whatHappened: {
        type: 'text',
        value:
          data.general.descriptions.whatHappened !== null &&
          data.general.descriptions.whatHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'whatHappened',
                '-',
              )
            : '-',
      },
      whereHappened: {
        type: 'text',
        value:
          data.general.descriptions.whereHappened !== null &&
          data.general.descriptions.whereHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'whereHappened',
                '-',
              )
            : '-',
      },
      whenHappened: {
        type: 'text',
        value:
          data.general.descriptions.whenHappened !== null &&
          data.general.descriptions.whenHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'whenHappened',
                '-',
              )
            : '-',
      },
      whoHappened: {
        type: 'text',
        value:
          data.general.descriptions.whoHappened !== null &&
          data.general.descriptions.whoHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'whoHappened',
                '-',
              )
            : '-',
      },
      whyHappened: {
        type: 'text',
        value:
          data.general.descriptions.whyHappened !== null &&
          data.general.descriptions.whyHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'whyHappened',
                '-',
              )
            : '-',
      },
      howHappened: {
        type: 'text',
        value:
          data.general.descriptions.howHappened !== null &&
          data.general.descriptions.howHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'howHappened',
                '-',
              )
            : '-',
      },
      internalDescription: {
        type: 'text',
        value:
          data.general.descriptions.internalDescription !== null &&
          data.general.descriptions.internalDescription !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'internalDescription',
                '-',
              )
            : '-',
      },
      media: {
        type: 'media',
        value: getItemOrDefaultToValue(data.details, 'media', []),
      },
      locations: {
        type: 'map-compound',
        value: data.locations != null ? getLocationValue(data) : [],
      },
      recommendations: {
        type: 'array',
        value:
          data.details.recommendations != null
            ? transformArrayDisplay(
                transformRecommendations(
                  getItemOrDefaultToValue(data.details, 'recommendations'),
                ),
              )
            : [],
      },
    },
    others: {
      rawValues: data,
      id: { value: getItemOrDefaultToValue(data.general, 'id') },
      site: {
        value: getItemOrDefaultToValue(data.details, 'site'),
      },
      regionId: {
        value: getItemOrDefaultToValue(data.general, 'regionId', '-'),
      },
      securityCompanyId: {
        value: getItemOrDefaultToValue(data.general, 'securityCompanyId', '-'),
      },
      startTime: {
        value: getItemOrDefaultToValue(data.general, 'eventStartTime'),
      },
      finishTime: {
        value: getItemOrDefaultToValue(data.general, 'eventFinishTime'),
      },
      locations: {
        value: data.locations !== null ? editableLocation(data) : [],
      },
      publicDescription: {
        value: getItemOrDefaultToValue(
          data.general.descriptions,
          'publicDescription',
        ),
      },
      archived: {
        value: getItemOrDefaultToValue(data.general, 'archived'),
      },
      whatHappened: {
        type: 'text',
        value:
          data.general.descriptions.whatHappened !== null &&
          data.general.descriptions.whatHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'whatHappened',
                '',
              )
            : '',
      },
      whereHappened: {
        type: 'text',
        value:
          data.general.descriptions.whereHappened !== null &&
          data.general.descriptions.whereHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'whereHappened',
                '',
              )
            : '',
      },
      whenHappened: {
        type: 'text',
        value:
          data.general.descriptions.whenHappened !== null &&
          data.general.descriptions.whenHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'whenHappened',
                '',
              )
            : '',
      },
      whoHappened: {
        type: 'text',
        value:
          data.general.descriptions.whoHappened !== null &&
          data.general.descriptions.whoHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'whoHappened',
                '',
              )
            : '',
      },
      whyHappened: {
        type: 'text',
        value:
          data.general.descriptions.whyHappened !== null &&
          data.general.descriptions.whyHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'whyHappened',
                '',
              )
            : '',
      },
      howHappened: {
        type: 'text',
        value:
          data.general.descriptions.howHappened !== null &&
          data.general.descriptions.howHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'howHappened',
                '',
              )
            : '',
      },
      internalDescription: {
        value: getItemOrDefaultToValue(
          data.general.descriptions,
          'internalDescription',
        ),
      },

      reporters: {
        value:
          data.details.sources.reporters != null
            ? transformArrayDisplay(
                getItemOrDefaultToValue(data.details.sources, 'reporters'),
                ['email'],
              )
            : [],
      },
      websites: {
        value:
          data.details.sources.websites != null
            ? transformArrayDisplay(
                getItemOrDefaultToValue(data.details.sources, 'websites'),
              )
            : [],
      },
      involvedAssets: {
        value:
          data.details.involvedAssets != null
            ? getItemOrDefaultToValue(data.details, 'involvedAssets').map(
                item => ({
                  assetName: {
                    value: getItemOrDefaultToValue(item, 'name'),
                    type: 'text',
                  },
                  id: {
                    value: getItemOrDefaultToValue(item, 'id'),
                    type: 'text',
                    display: 'none',
                  },
                  assetId: {
                    label: 'Asset ID',
                    value: getItemOrDefaultToValue(item, 'assetId'),
                    type: 'text',
                  },
                  category: {
                    value: getItemOrDefaultToValue(item.type, 'category'),
                    type: 'text',
                  },
                  typeId: {
                    value: getItemOrDefaultToValue(item.type, 'id'),
                    type: 'text',
                    display: 'none',
                  },
                  assetCategoryName: {
                    value: getItemOrDefaultToValue(item.type, 'name'),
                    type: 'text',
                  },
                  typeName: {
                    value: typeNameConstructor(
                      item.type.name,
                      item.type.assetTypeId,
                    ),
                    type: 'text',
                  },
                  originalTypeName: {
                    value: getItemOrDefaultToValue(item.type, 'name'),
                    display: 'none',
                  },
                }),
              )
            : [],
      },
      involvedPeople: {
        value:
          data.details.involvedPeople != null
            ? transformArrayDisplay(
                getItemOrDefaultToValue(data.details, 'involvedPeople'),
                ['email'],
              )
            : [],
      },

      involvedCompanies: {
        value:
          data.details.involvedCompanies != null
            ? transformArrayDisplay(
                getItemOrDefaultToValue(data.details, 'involvedCompanies'),
                ['id'],
              )
            : [],
      },
      media: {
        value:
          data.details.media != null &&
          assignUniqueId(getItemOrDefaultToValue(data.details, 'media')),
      },
      creator: {
        value: getItemOrDefaultToValue(data.general, 'creator'),
      },
      publishing: {
        value: getItemOrDefaultToValue(data, 'publishing'),
      },
      assignee: {
        value:
          data.operational.assignedTo !== null
            ? getPersonName(data.operational.assignedTo.general)
            : '',
      },
      assigneeId: {
        value:
          data.operational.assignedTo !== null
            ? getItemOrDefaultToValue(
                data.operational.assignedTo.general,
                'id',
                '',
              )
            : '',
      },
      auditStatus: {
        value:
          data.publishing.statusAudit !== null
            ? data.publishing.statusAudit
            : null,
      },
      followupEvents: {
        value: data.followupEvents
          ? getItemOrDefaultToValue(data, 'followupEvents', []).map(event => ({
              ...event,
              sendToServer: false,
            }))
          : [],
      },
      followUptask: {
        value: data.followupTasks
          ? getItemOrDefaultToValue(data, 'followupTasks', []).map(task => ({
              joins: [],
              tasks: [task],
              surfaces: [],
              rootTaskId: task.id,
              sendToServer: false, // this mean that on edit you need to send an api call to fetch the data from the server
            }))
          : [],
      },
      ...(data.parentEvent && {
        parentEventId: {
          value: getItemOrDefaultToValue(data.parentEvent.general, 'eventId'),
        },
        parentId: {
          value: getItemOrDefaultToValue(data.parentEvent.general, 'id'),
        },
        parentEventCategory: {
          value: getItemOrDefaultToValue(data.parentEvent.general, 'category'),
        },
        parentEventType: {
          value: getItemOrDefaultToValue(data.parentEvent.general, 'type'),
        },
        parentStartTime: {
          value: getItemOrDefaultToValue(
            data.parentEvent.general,
            'eventStartTime',
          ),
        },
        parentFinishTime: {
          value: getItemOrDefaultToValue(
            data.parentEvent.general,
            'eventFinishTime',
          ),
        },
        parentSeverityRating: {
          value: getItemOrDefaultToValue(
            data.parentEvent.general,
            'severityRating',
          ),
        },
        parentSummary: {
          value: getItemOrDefaultToValue(
            data.parentEvent.general.descriptions,
            'publicDescription',
          ),
        },
        parentSeverity: {
          value: getItemOrDefaultToValue(data.parentEvent.general, 'severity'),
        },
        parentWhat: {
          value: getItemOrDefaultToValue(
            data.parentEvent.general.descriptions,
            'whatHappened',
          ),
        },
        parentWho: {
          value: getItemOrDefaultToValue(
            data.parentEvent.general.descriptions,
            'whoHappened',
          ),
        },
        parentWhy: {
          value: getItemOrDefaultToValue(
            data.parentEvent.general.descriptions,
            'whyHappened',
          ),
        },
        parentHow: {
          value: getItemOrDefaultToValue(
            data.parentEvent.general.descriptions,
            'howHappened',
          ),
        },
      }),
    },
  };
};

export const transformParentDetails = data => {
  return {
    parentDetails: {
      ...(data.parentEvent && {
        parentEventId: getItemOrDefaultToValue(
          data.parentEvent.general,
          'eventId',
        ),
        parentEventTitle: getItemOrDefaultToValue(
          data.parentEvent.general,
          'title',
        ),

        parentEventCategory: getItemOrDefaultToValue(
          data.parentEvent.general,
          'category',
        ),

        parentEventType: getItemOrDefaultToValue(
          data.parentEvent.general,
          'type',
        ),

        parentStartTime: getItemOrDefaultToValue(
          data.parentEvent.general,
          'eventStartTime',
        ),

        parentFinishTime: getItemOrDefaultToValue(
          data.parentEvent.general,
          'eventFinishTime',
        ),

        parentSeverityRating: getItemOrDefaultToValue(
          data.parentEvent.general,
          'severityRating',
        ),

        parentSummary: getItemOrDefaultToValue(
          data.parentEvent.general.descriptions,
          'publicDescription',
        ),

        parentSeverity: getItemOrDefaultToValue(
          data.parentEvent.general,
          'severity',
        ),

        parentWhat: getItemOrDefaultToValue(
          data.parentEvent.general.descriptions,
          'whatHappened',
        ),
        parentWhere: getItemOrDefaultToValue(
          data.parentEvent.general.descriptions,
          'whereHappened',
        ),
        parentWhen: getItemOrDefaultToValue(
          data.parentEvent.general.descriptions,
          'whenHappened',
        ),
        parentWho: getItemOrDefaultToValue(
          data.parentEvent.general.descriptions,
          'whoHappened',
        ),
        parentWhy: getItemOrDefaultToValue(
          data.parentEvent.general.descriptions,
          'whyHappened',
        ),

        parentHow: getItemOrDefaultToValue(
          data.parentEvent.general.descriptions,
          'howHappened',
        ),
      }),
    },
  };
};

export const transformedSectionedEvents = data => {
  if (isEmpty(data)) {
    return {};
  }
  const auditStatus = data.publishing.statusAudit
    ? data.publishing.statusAudit.map(item => ({
        date: displayTime({ timestamp: item.timestamp }),
        action: item.detail.action,
        employeeId: item.detail.user.employeeId,
        name: item.detail.user.name,
        role: {
          data: item.detail.user.roles
            ? formatRoleNames(item.detail.user.roles)
            : DEFAULT_EMPTY_VALUE,
        },
      }))
    : [];
  const followupReports = data.followupEvents
    ? data.followupEvents.map(item => ({
        id: item.general.id, // add id key to detail view object if you want it to be clickable from detail panel
        eventId: item.general.eventId,
        title: item.general.title,
        eventCategory: t(item.general.category),
        eventType: t(item.general.type),
        eventStartTime: item.general.eventStartTime,
        eventFinishTime: item.general.eventFinishTime,
      }))
    : [];
  const followupTasks = data.followupTasks
    ? data.followupTasks.map(item => ({
        description: item.description,
        taskType: t(formatTaskCategoryName(item.taskType)),
        status: t(item.status),
        name: item.name,
        evidence: item.evidence ? item.evidence.length : null,
      }))
    : [];
  return {
    noSectionFields: {
      dateReported: {
        type: 'text',
        value: displayTime({
          timestamp: getItemOrDefaultToValue(data, 'createdAt'),
        }),
      },
      eventId: {
        type: 'text',
        label: 'Report ID',
        value: getItemOrDefaultToValue(data.general, 'eventId', '-'),
      },
      eventCategory: {
        type: 'textTranslated',
        value: getItemOrDefaultToValue(data.general, 'category', '-'),
      },
      title: {
        type: 'text',
        value: getItemOrDefaultToValue(data.general, 'title', '-'),
      },
      eventType: {
        type: 'textTranslated',
        value: getItemOrDefaultToValue(data.general, 'type', '-'),
      },
      severity: {
        type: 'text',
        label: 'Severity Description',
        value:
          data.general.severity !== null && data.general.severity !== ''
            ? getItemOrDefaultToValue(data.general, 'severity', '')
            : '',
      },
      severityRating: {
        type: 'textTranslated',
        value:
          data.general.severityRating !== null &&
          data.general.severityRating !== ''
            ? converSeverityRatingToText(
                getItemOrDefaultToValue(
                  data.general,
                  'severityRating',
                  severityRatingEnums.notRated,
                ),
              )
            : severityRatingEnums.notRated,
      },
      archived: {
        type: 'boolean',
        value: data.general.archived,
      },
    },
    auditTrailSection: {
      auditStatus: {
        type: 'list-data',
        value: {
          data: auditStatus,
          columns: AuditTableColumns,
        },
      },
    },
    overviewSection: {
      incidentStartTime: {
        type: 'text',
        value: displayTime({
          timestamp: getItemOrDefaultToValue(data.general, 'eventStartTime'),
        }),
      },
      incidentFinishTime: {
        type: 'text',
        value: displayTime({
          timestamp: getItemOrDefaultToValue(data.general, 'eventFinishTime'),
        }),
      },
      summary: {
        type: 'text',
        value: getItemOrDefaultToValue(
          data.general.descriptions,
          'publicDescription',
          '-',
        ),
      },
    },
    locationSection: {
      location: {
        type: 'map-compound',
        value: data.locations != null ? getLocationValue(data) : [],
      },
    },
    descriptionSection: {
      'whatHappened?': {
        label: 'What happened?',
        type: 'text',
        value:
          data.general.descriptions.whatHappened !== null &&
          data.general.descriptions.whatHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'whatHappened',
                '-',
              )
            : '-',
      },
      'whereDidItHappen?': {
        label: 'Where did it happen?',
        type: 'text',
        value:
          data.general.descriptions.whereHappened !== null &&
          data.general.descriptions.whereHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'whereHappened',
                '-',
              )
            : '-',
      },
      'whenDidItHappen?': {
        label: 'When did it happen?',
        type: 'text',
        value:
          data.general.descriptions.whenHappened !== null &&
          data.general.descriptions.whenHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'whenHappened',
                '-',
              )
            : '-',
      },
      'whoWasInvolved?': {
        label: 'Who was involved?',
        type: 'text',
        value:
          data.general.descriptions?.whoWasInvolved !== null &&
          data.general.descriptions?.whoWasInvolved !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'whoHappened',
                '-',
              )
            : '-',
      },
      'whyDidItHappen?': {
        label: 'Why did it happen?',
        type: 'text',
        value:
          data.general.descriptions.whyHappened !== null &&
          data.general.descriptions.whyHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'whyHappened',
                '-',
              )
            : '-',
      },
      'howDidItHappen?': {
        label: 'How did it happen?',
        type: 'text',
        value:
          data.general.descriptions.howHappened !== null &&
          data.general.descriptions.howHappened !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'howHappened',
                '-',
              )
            : '-',
      },
      internalNotes: {
        type: 'text',
        value:
          data.general.descriptions.internalDescription !== null &&
          data.general.descriptions.internalDescription !== ''
            ? getItemOrDefaultToValue(
                data.general.descriptions,
                'internalDescription',
                '-',
              )
            : '-',
      },
    },
    incidentReviewSection: {
      followupReports: {
        label: 'Follow-up Reports',
        type: 'list-data',
        value: {
          data: followupReports,
          columns: FollowupReprtTableColumns,
        },
        view: 'events',
        enableFollowupButton: true,
      },
      followupTasks: {
        label: 'Follow-up Tasks',
        type: 'list-data',
        value: {
          data: followupTasks,
          columns: FollowupTaskTableHeaders,
        },
        view: 'cleaningTasks',
        enableFollowupButton: true,
      },
      recommendations: {
        type: 'array',
        noButton: true,
        value: createDisplay(
          transformRecommendationsEnum(
            getItemOrDefaultToValue(data.details, 'recommendations', []),
          ),
          ['audience', 'description'],
        ),
      },
    },
    detailedInfo: {
      websites: {
        type: 'array',
        value: createURL(
          getItemOrDefaultToValue(data.details.sources, 'websites', []),
          ['url'],
        ),
      },
      involvedAssets: {
        type: 'array',
        value: nestedDisplay(
          getItemOrDefaultToValue(data.details, 'involvedAssets', []),
        ),
      },
      involvedPeople: {
        type: 'array',
        value: createDisplay(
          getItemOrDefaultToValue(data.details, 'involvedPeople', []),
          ['involvement', 'firstName', 'lastName'],
        ),
      },

      involvedCompanies: {
        type: 'array',
        value:
          data.details.involvedCompanies !== null
            ? nestedDisplay(
                getItemOrDefaultToValue(data.details, 'involvedCompanies'),
                ['id'],
              )
            : [],
      },
      media: {
        type: 'media',
        value:
          data.details.media !== null &&
          getItemOrDefaultToValue(data.details, 'media'),
      },
      publishing: {
        value: getItemOrDefaultToValue(data, 'publishing'),
      },
    },
    others: {
      rawData: data,
      id: { value: getItemOrDefaultToValue(data.general, 'id') },
      site: {
        value: getItemOrDefaultToValue(data.details, 'site'),
      },
    },
    ...(data.parentEvent !== null && {
      // only show if this event is a followup event
      originalReportOverviewSection: {
        eventId: {
          type: 'text',
          label: 'Report ID',
          value: getItemOrDefaultToValue(data.parentEvent.general, 'eventId'),
        },
        dateReported: {
          type: 'text',
          value: displayTime({
            timestamp: getItemOrDefaultToValue(data.parentEvent, 'createdAt'),
          }),
        },
        eventCategory: {
          type: 'textTranslated',
          value: getItemOrDefaultToValue(
            data.parentEvent.general,
            'category',
            '-',
          ),
        },
        title: {
          type: 'text',
          value: getItemOrDefaultToValue(
            data.parentEvent.general,
            'title',
            '-',
          ),
        },
        eventType: {
          type: 'textTranslated',
          value: getItemOrDefaultToValue(data.parentEvent.general, 'type', '-'),
        },
        summary: {
          type: 'text',
          value: getItemOrDefaultToValue(
            data.parentEvent.general.descriptions,
            'publicDescription',
          ),
        },
        originalIncidentStartTime: {
          type: 'text',
          value: displayTime({
            timestamp: getItemOrDefaultToValue(
              data.parentEvent.general,
              'eventStartTime',
            ),
          }),
        },
        originalIncidentFinishTime: {
          type: 'text',
          value: displayTime({
            timestamp: getItemOrDefaultToValue(
              data.parentEvent.general,
              'eventFinishTime',
            ),
          }),
        },
        whatHappened: {
          label: 'What happened?',
          type: 'text',
          value: getItemOrDefaultToValue(
            data.parentEvent.general.descriptions,
            'whatHappened',
            '-',
          ),
        },
        whereDidItHappen: {
          label: 'Where did it happen?',
          type: 'text',
          value: getItemOrDefaultToValue(
            data.parentEvent.general.descriptions,
            'whereHappened',
            '-',
          ),
        },
        whenDidItHappen: {
          label: 'When did it happen?',
          type: 'text',
          value: getItemOrDefaultToValue(
            data.parentEvent.general.descriptions,
            'whenHappened',
            '-',
          ),
        },
        whoWasInvolved: {
          label: 'Who was involved?',
          type: 'text',
          value: getItemOrDefaultToValue(
            data.parentEvent.general.descriptions,
            'whoHappened',
            '-',
          ),
        },
        whyDidItHappen: {
          type: 'text',
          value: getItemOrDefaultToValue(
            data.parentEvent.general.descriptions,
            'whyHappened',
            '-',
          ),
        },
        howDidItHappen: {
          label: 'How did it happen?',
          type: 'text',
          value: getItemOrDefaultToValue(
            data.parentEvent.general.descriptions,
            'howHappened',
            '-',
          ),
        },
      },
    }),
  };
};
